export const API_NETWORK_FAILURE_EVENT = 'timelining:api-network-failure'

export type ApiNetworkFailureSource = 'fetch' | 'promise' | 'manual'

export type ApiNetworkFailureDetail = {
  id: string
  at: number
  source: ApiNetworkFailureSource
  url?: string
  method?: string
  message: string
}

export type ApiFailureReason = 'network' | 'appcheck' | 'unauthenticated' | 'unknown'

export type ApiDegradeResult<T> =
  | { ok: true; data: T }
  | { ok: false; reason: ApiFailureReason; error: unknown }

type FailureListener = (detail: ApiNetworkFailureDetail) => void

let originalFetch: typeof fetch | null = null
let installCount = 0

const OFFLINE_ERROR_MARKERS = [
  'err_internet_disconnected',
  'internet connection appears to be offline',
  'network request failed',
  'networkerror',
  'network error',
  'load failed',
]

const WEAK_NETWORK_ERROR_MARKERS = [
  'failed to fetch',
  'could not fetch',
  'fetch failed',
  'client is offline',
  'offline',
]

const FIREBASE_UNAVAILABLE_CODES = new Set([
  'unavailable',
  'functions/unavailable',
  'firestore/unavailable',
])

const readNavigatorOnline = (): boolean | null => {
  if (typeof navigator === 'undefined' || typeof navigator.onLine !== 'boolean') return null
  return navigator.onLine
}

const readErrorCode = (error: unknown): string => {
  if (!error || typeof error !== 'object') return ''
  const code = (error as { code?: unknown }).code
  return typeof code === 'string' ? code.toLowerCase() : ''
}

const readErrorName = (error: unknown): string => {
  if (!error || typeof error !== 'object') return ''
  const name = (error as { name?: unknown }).name
  return typeof name === 'string' ? name.toLowerCase() : ''
}

export const getApiErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message || error.name
  if (typeof error === 'string') return error
  if (error && typeof error === 'object') {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string') return message
  }
  return 'Network request failed.'
}

export const isInternetDisconnectedApiError = (
  error: unknown,
  options?: { navigatorOnline?: boolean | null },
): boolean => {
  if (readErrorName(error) === 'aborterror') return false

  const message = getApiErrorMessage(error).toLowerCase()
  const code = readErrorCode(error)
  const navigatorOnline = options?.navigatorOnline ?? readNavigatorOnline()

  if (OFFLINE_ERROR_MARKERS.some((marker) => message.includes(marker))) return true

  const hasWeakNetworkSignal =
    WEAK_NETWORK_ERROR_MARKERS.some((marker) => message.includes(marker)) ||
    FIREBASE_UNAVAILABLE_CODES.has(code)

  return hasWeakNetworkSignal && navigatorOnline === false
}

const detailFromFailure = (
  error: unknown,
  source: ApiNetworkFailureSource,
  meta?: Pick<ApiNetworkFailureDetail, 'url' | 'method'>,
): ApiNetworkFailureDetail => ({
  id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
  at: Date.now(),
  source,
  url: meta?.url,
  method: meta?.method,
  message: getApiErrorMessage(error),
})

export const dispatchApiNetworkFailure = (detail: ApiNetworkFailureDetail): void => {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent<ApiNetworkFailureDetail>(API_NETWORK_FAILURE_EVENT, { detail }))
}

export const notifyApiFailureIfOffline = (
  error: unknown,
  source: ApiNetworkFailureSource = 'manual',
  meta?: Pick<ApiNetworkFailureDetail, 'url' | 'method'>,
): boolean => {
  if (!isInternetDisconnectedApiError(error)) return false
  dispatchApiNetworkFailure(detailFromFailure(error, source, meta))
  return true
}

export const classifyApiFailureReason = (error: unknown): ApiFailureReason => {
  const code = readErrorCode(error)
  const message = getApiErrorMessage(error).toLowerCase()
  if (isInternetDisconnectedApiError(error, { navigatorOnline: readNavigatorOnline() })) return 'network'
  if (
    code.includes('app-check') ||
    code.includes('appcheck') ||
    message.includes('app check') ||
    message.includes('recaptcha') ||
    message.includes('cors')
  ) return 'appcheck'
  if (code.includes('unauthenticated') || message.includes('unauthenticated') || message.includes('auth')) return 'unauthenticated'
  if (FIREBASE_UNAVAILABLE_CODES.has(code) || message.includes('failed to fetch') || message.includes('network request failed')) return 'network'
  return 'unknown'
}

export const safeApiCall = async <T,>(
  operation: () => Promise<T>,
): Promise<ApiDegradeResult<T>> => {
  try {
    return { ok: true, data: await operation() }
  } catch (error) {
    notifyApiFailureIfOffline(error, 'manual')
    return { ok: false, reason: classifyApiFailureReason(error), error }
  }
}

export const subscribeApiNetworkFailures = (listener: FailureListener): (() => void) => {
  if (typeof window === 'undefined') return () => {}
  const handle = (event: Event) => {
    listener((event as CustomEvent<ApiNetworkFailureDetail>).detail)
  }
  window.addEventListener(API_NETWORK_FAILURE_EVENT, handle)
  return () => window.removeEventListener(API_NETWORK_FAILURE_EVENT, handle)
}

const requestMeta = (input: RequestInfo | URL, init?: RequestInit): Pick<ApiNetworkFailureDetail, 'url' | 'method'> => {
  const requestCtor = typeof Request === 'undefined' ? null : Request
  const isRequest = requestCtor ? input instanceof requestCtor : false
  const request = isRequest ? input as Request : null
  return {
    url: request?.url ?? String(input),
    method: init?.method ?? request?.method ?? 'GET',
  }
}

export const installApiFailureCapture = (): (() => void) => {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return () => {}

  installCount += 1
  if (!originalFetch) {
    originalFetch = window.fetch
    window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
      const meta = requestMeta(input, init)
      return originalFetch!.call(window, input, init).catch((error: unknown) => {
        notifyApiFailureIfOffline(error, 'fetch', meta)
        throw error
      })
    }) as typeof fetch
  }

  return () => {
    installCount = Math.max(0, installCount - 1)
    if (installCount === 0 && originalFetch) {
      window.fetch = originalFetch
      originalFetch = null
    }
  }
}

export const installUnhandledApiFailureCapture = (): (() => void) => {
  if (typeof window === 'undefined') return () => {}
  const handle = (event: PromiseRejectionEvent) => {
    notifyApiFailureIfOffline(event.reason, 'promise')
  }
  window.addEventListener('unhandledrejection', handle)
  return () => window.removeEventListener('unhandledrejection', handle)
}
